import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, BarChart3 } from 'lucide-react';
import { labTATService } from '../../services/labTATService';
import toast from 'react-hot-toast';

const TATDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadMetrics();
  }, [dateRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const data = await labTATService.getDashboardMetrics(dateRange);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading TAT metrics:', error);
      toast.error('Failed to load TAT metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No metrics available</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TAT Dashboard</h1>
          <p className="text-gray-600">Turnaround Time Performance Metrics</p>
        </div>
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Total Orders</div>
            <BarChart3 className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{metrics.totalOrders}</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Within Target</div>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-600">{metrics.withinTarget}</div>
          <div className="text-sm text-gray-500 mt-1">
            {getPercentage(metrics.withinTarget, metrics.totalOrders)}%
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Near Breach</div>
            <TrendingUp className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="text-3xl font-bold text-yellow-600">{metrics.nearBreach}</div>
          <div className="text-sm text-gray-500 mt-1">
            {getPercentage(metrics.nearBreach, metrics.totalOrders)}%
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Breached</div>
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div className="text-3xl font-bold text-red-600">{metrics.breached}</div>
          <div className="text-sm text-gray-500 mt-1">
            {getPercentage(metrics.breached, metrics.totalOrders)}%
          </div>
        </div>
      </div>

      {/* Average TAT */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-semibold text-gray-900">Average Turnaround Time</h2>
        </div>
        <div className="text-4xl font-bold text-teal-600">
          {formatTime(metrics.averageTAT)}
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Department Breakdown</h2>
          <div className="space-y-4">
            {Object.entries(metrics.departmentBreakdown || {}).map(([dept, data]: [string, any]) => (
              <div key={dept} className="border-b border-gray-200 pb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">{dept}</span>
                  <span className="text-sm text-gray-600">{data.count} orders</span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ {data.withinTarget}</span>
                  <span className="text-yellow-600">⚠ {data.nearBreach}</span>
                  <span className="text-red-600">✗ {data.breached}</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Avg TAT: {formatTime(data.avgTAT)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Priority Breakdown</h2>
          <div className="space-y-4">
            {Object.entries(metrics.priorityBreakdown || {}).map(([priority, data]: [string, any]) => (
              <div key={priority} className="border-b border-gray-200 pb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900 capitalize">{priority}</span>
                  <span className="text-sm text-gray-600">{data.count} orders</span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ {data.withinTarget}</span>
                  <span className="text-yellow-600">⚠ {data.nearBreach}</span>
                  <span className="text-red-600">✗ {data.breached}</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Avg TAT: {formatTime(data.avgTAT)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Indicator */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Performance</h2>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-teal-600 bg-teal-200">
                TAT Compliance
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold inline-block text-teal-600">
                {getPercentage(metrics.withinTarget, metrics.totalOrders)}%
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-teal-200">
            <div
              style={{ width: `${getPercentage(metrics.withinTarget, metrics.totalOrders)}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-teal-600"
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TATDashboard;
